import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Button } from '@material-ui/core';
import { Checkbox, Typography } from '@coolremote/shared';
import { t } from 'ttag';
import FiltersList from './components/FiltersList';
import useStyles from './FilterOutSpecificErrorCodesForm.styles';


/**
 * Validates a string of error codes separated by commas.
 * Each error code must consist of alphanumeric characters only.
 *
 * @param {string} codes - A string containing error codes separated by commas.
 * @returns {boolean} - Returns `true` if the input string matches the expected format, otherwise `false`.
 */
const validateCodes = (codes) => {
  const regex = /^[A-Za-z0-9]+(,[A-Za-z0-9]+)*$/;
  return regex.test(codes);
};


/**
 * @typedef {Object} Filter
 * @property {string} brand - The brand associated with the filter.
 * @property {Array<string>} codes - The error codes associated with the filter.
 * @property {string} type - The unit type associated with the filter.
 */


/**
 * Validates an array of filter objects to ensure they meet specific criteria.
 *
 * @param {Filter[]} [filters=[]] - An array of filter objects to validate.
 * @returns {ValidationResult} An object containing the validation results.
 */
export const validateFilters = (filters = []) => {
  const validationResults = filters.map(filter => ({
    brand: !!filter.brand,
    codes: validateCodes(filter.codes),
    type: !!filter.type,
  }));

  const isValid = filters.length > 0 && validationResults.every(result => result.brand && result.codes && result.type);

  return {
    isValid,
    details: validationResults,
  };
};

/**
 * @typedef {Object} ValidationResult
 * @property {boolean} isValid - Indicates whether all filters are valid.
 * @property {Array<ValidationDetail>} details - An array of validation results for each filter.
 */

/**
 * @typedef {Object} ValidationDetail
 * @property {boolean} brand - Indicates if the brand is valid.
 * @property {boolean} codes - Indicates if the codes are valid.
 * @property {boolean} type - Indicates if the type is valid.
 */

/**
 * @typedef {Object} FilterOutSpecificErrorCodesFormProps
 * @property {boolean} enabled - Whether the filter is enabled.
 * @property {function(boolean):void} onEnable - Callback to handle enabling/disabling the filter.
 * @property {Array<Filter>} filters - Array of filter objects.
 * @property {function(Array<Filter>):void} onFilterChange - Callback to handle filter changes.
 * @property {ValidationResult} validationStatus - Validation status of the filters.
 */

/**
 * FilterOutSpecificErrorCodesForm component.
 *
 * @component
 * @param {FilterOutSpecificErrorCodesFormProps} props - Component props.
 * @returns {JSX.Element}
 */
const FilterOutSpecificErrorCodesForm = ({ enabled, onEnable, filters = [], onFilterChange, validationStatus, disabled = false }) => {
  const classes = useStyles();


  useEffect(() => {
    if (enabled) {
      onFilterChange(filters);
    }
  }, [filters]);

  const handleAddFilter = () => {
    const newFilter = {
      brand: '',
      codes: '',
      type: '',
    };
    onFilterChange([...filters, newFilter]);
  };

  const handleDeleteFilter = (index) => {
    onFilterChange(filters.filter((_, i) => i !== index));
  };

  const handleCheckboxChange = () => {
    if (disabled) {
      return;
    }
    const nextChecked = !enabled;
    if(filters.length === 0 && nextChecked) {
      handleAddFilter();
    }
    onEnable(nextChecked);
  };

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <div className={classes.checkboxLabelRow}>
          <Checkbox
            checked={enabled}
            onChange={handleCheckboxChange}
            disabled={disabled}
          />
          <Typography variant="body2">
            {filters.length ?
              (filters.length > 1 ?
                t`Filter Out Specific Error Codes (%{count} Codes)`.replace('%{count}', filters.length) :
                t`Filter Out Specific Error Codes (%{count} Code)`.replace('%{count}', filters.length)) :
              t`Filter Out Specific Error Codes (%{count} Codes)`.replace('%{count}', filters.length)}
          </Typography>
        </div>

       {enabled  &&  <Button variant="text" color="primary" onClick={handleAddFilter} disabled={!enabled || disabled}>
          {t`+ Add`}
        </Button>}
      </div>
      <div>
        <FiltersList validity={validationStatus} filters={filters} onFilterDelete={handleDeleteFilter} disabled={!enabled || disabled} />
      </div>
    </div>
  );
};



FilterOutSpecificErrorCodesForm.propTypes = {
  enabled: PropTypes.bool.isRequired,
  onEnable: PropTypes.func.isRequired,
  onFilterChange: PropTypes.func.isRequired,
  filters: PropTypes.arrayOf(PropTypes.object),
  validationStatus: PropTypes.shape({
    isValid: PropTypes.bool,
    details: PropTypes.array,
  }),
  disabled: PropTypes.bool,
};

FilterOutSpecificErrorCodesForm.defaultProps = {
  disabled: false,
};

export default FilterOutSpecificErrorCodesForm;
